import React, { useState } from "react";
import Button from "@components/Button/Button";
import { FileUpload } from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import FormItem from "@components/FormItem/FormItem";
import { useNotification } from "@context/NotificationContext";
import {
  drybulkCargoOperationsService,
  type StagedAttachment,
} from "@services/drybulkCargoOperationsApi";
import { useQueryClient } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { FormProvider, useFieldArray, useWatch, type UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  DRYBULK_CARGO_OPS_LANG_BASE,
  DRYBULK_CARGO_OPS_QUERY_KEYS,
  DRYBULK_CARGO_OPS_TELEMETRY,
  GENERAL_ATTACHMENT_TYPES,
} from "../constants";
import { useCargoOperationDetail } from "../hooks/useCargoOperationDetail";
import { useDownloadCargoOperationAttachment } from "../hooks/useDownloadCargoOperationAttachment";
import { getAttachmentFileName } from "../utils/attachmentFileName";
import { problemDetail } from "../utils/problemDetail";
import OperationAttachmentsList from "./OperationAttachmentsList";

const L = `${DRYBULK_CARGO_OPS_LANG_BASE}.documents`;

/** The Documents tab's unsent files: owned by the operation page, so they survive a tab switch. */
export interface PendingDocumentsForm {
  rows: { staged: StagedAttachment; attachmentType: string }[];
}

interface OperationDocumentsTabProps {
  operationId: string;
  form: UseFormReturn<PendingDocumentsForm>;
}

const OperationDocumentsTab: React.FC<OperationDocumentsTabProps> = ({ operationId, form }) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const queryClient = useQueryClient();
  const profiles = useFileUploadProfiles();
  const download = useDownloadCargoOperationAttachment();
  const { fields, append, remove } = useFieldArray({ control: form.control, name: "rows" });
  const rows = useWatch({ control: form.control, name: "rows" }) ?? [];
  const [uploading, setUploading] = useState(0);
  const [saving, setSaving] = useState(false);
  const detail = useCargoOperationDetail(operationId, { paused: fields.length > 0 });

  const conditions = { ...profiles.drybulkCargoOpsDocument, hideErrors: false };
  const allowedExtensions = conditions.format.map((ext) => `.${ext}`);

  const typeOptions = GENERAL_ATTACHMENT_TYPES.map((value) => ({
    value,
    label: t(`${L}.types.${value}`),
  }));

  const handleUpload = async (files: File[]) => {
    setUploading((n) => n + files.length);
    await Promise.all(
      files.map(async (file) => {
        try {
          append({
            staged: await drybulkCargoOperationsService.stageAttachment(file, ""),
            attachmentType: "",
          });
        } catch {
          notify({
            type: "error",
            title: t("common.error"),
            description: t(`${L}.upload-failed`),
          });
        } finally {
          setUploading((n) => n - 1);
        }
      }),
    );
  };

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      for (const row of [...form.getValues("rows")]) {
        try {
          await runTransaction(
            {
              module: DRYBULK_CARGO_OPS_TELEMETRY.module,
              screen: DRYBULK_CARGO_OPS_TELEMETRY.documentsScreen,
              action: "attach_cargo_operation_document",
              entityType: "cargo_operation",
              entityId: operationId,
            },
            (transactionId) =>
              drybulkCargoOperationsService.uploadAttachment(
                operationId,
                { ...row.staged, attachmentType: row.attachmentType },
                { transactionId },
              ),
          );
        } catch (error) {
          notify({
            type: "error",
            title: t("common.error"),
            description: problemDetail(error) ?? t(`${L}.attach-failed`),
          });
          return;
        }
        const index = form
          .getValues("rows")
          .findIndex((current) => current.staged.documentUrl === row.staged.documentUrl);
        if (index >= 0) remove(index);
      }
    } finally {
      setSaving(false);
      void queryClient.invalidateQueries({
        queryKey: DRYBULK_CARGO_OPS_QUERY_KEYS.detail(operationId),
      });
    }
  };

  const saveDisabled =
    fields.length === 0 || uploading > 0 || saving || rows.some((row) => !row?.attachmentType);

  return (
    <FormProvider {...form}>
      <div className="flex flex-col gap-4" data-testid="drybulk-documents-tab">
        <h3 className="text-base font-semibold">{t(`${L}.title`)}</h3>
        <OperationAttachmentsList
          attachments={detail.data?.attachments ?? []}
          isDownloading={download.isBusy}
          onView={(attachment) => void download.openAttachment(attachment.documentUrl)}
          onDownload={(attachment) =>
            void download.downloadAttachment(
              attachment.documentUrl,
              getAttachmentFileName(attachment),
            )
          }
        />

        <FileUpload
          testId="drybulk-documents-upload"
          maxFiles={10}
          allowedExtensions={allowedExtensions}
          conditions={conditions}
          files={fields.map((_, index) => ({
            name: rows[index]?.staged.originalFileName ?? "",
            url: rows[index]?.staged.documentUrl,
          }))}
          setFiles={() => undefined}
          disabled={uploading > 0 || saving}
          uploadDescription={t("upload.additional-documents-upload", {
            extensions: conditions.format.join(", "),
            size: "10MB",
          })}
          onUpload={(files) => void handleUpload(files)}
        />

        {fields.map((field, index) => (
          <div
            key={field.id}
            className="flex flex-col gap-3"
            data-testid={`drybulk-documents-pending-${index}`}
          >
            <SingleFile
              testId={`drybulk-documents-pending-${index}-file`}
              file={{ name: rows[index]?.staged.originalFileName ?? "" }}
              fieldName="drybulk-documents-pending"
              setFiles={() => undefined}
              removeFn={() => remove(index)}
              disabled={saving}
            />
            <FormItem<PendingDocumentsForm, `rows.${number}.attachmentType`>
              required
              testId={`drybulk-documents-pending-${index}-type`}
              name={`rows.${index}.attachmentType`}
              type="select"
              label={t(`${L}.document-type`)}
              options={typeOptions}
            />
          </div>
        ))}

        {fields.length > 0 && (
          <div>
            <Button
              variant="contained"
              disabled={saveDisabled}
              onClick={() => void save()}
              testId="drybulk-documents-save"
              label={<span className="normal-case font-medium">{t(`${L}.save`)}</span>}
            />
          </div>
        )}
      </div>
    </FormProvider>
  );
};

export default OperationDocumentsTab;
